export const researchNotes202609 = [
  {
    "category": "Contact control",
    "description": "How FWBC-VLA uses joint signals during door opening and wiping, what its wheeled-legged tests show, and how to test force attribution.",
    "introduction": [
      "A robot pulling a door needs to keep some contact forces and resist others. The handle pushes back against the gripper as the door opens. A shove against the body can produce a similar force magnitude while requiring a different response. A useful contact representation has to retain the location, direction and timing of the load.",
      "FWBC-VLA, submitted in September 2026, studies this problem on a wheeled-legged robot with an arm. Its results belong to that platform and the reported tasks. Applying the same approach to a biped introduces another set of balance and support-contact tests."
    ],
    "keywords": [
      "FWBC-VLA",
      "force-aware robot control",
      "whole-body compensation",
      "Physical AI"
    ],
    "methodology": [
      "Research analysis based on the linked paper and project material. The force-attribution evaluation proposed in this article is an editorial suggestion."
    ],
    "relatedLinks": [
      {
        "label": "Whole-body loco-manipulation",
        "path": "/robotics-news/whole-body-loco-manipulation"
      },
      {
        "label": "Humanoid controller architecture",
        "path": "/humanoid-robot-controller-architecture"
      },
      {
        "label": "Testing standards",
        "path": "/humanoid-robot-testing-standards"
      }
    ],
    "sections": [
      {
        "heading": "What the researchers built",
        "paragraphs": [
          "The framework estimates contact from the robot's internal signals. HSR-Force uses residual joint torque to infer contact strength and how it changes over time. Those estimates enter the VLA action expert as tokens. A separate compensation generator combines proprioception, estimated contact state and a body-frame force estimate before a whole-body controller executes the combined command.",
          "The physical platform is a DeepRobotics M20S wheeled-legged quadruped with a six-joint CM1 arm. The authors evaluate whiteboard wiping and door opening. In the door-with-closer task, FWBC-VLA reaches 52 percent at the final crossing stage, compared with 12 percent for ForceVLA in the reported protocol. These are author-reported results; TechniaHQRobot has not rerun the system."
        ]
      },
      {
        "heading": "Why one force number loses information",
        "paragraphs": [
          "Consider an illustrative pair of 20 N loads. One acts at the hand. The other acts against the torso. Their effects depend on their directions and distances from the body's support contacts. Even two equal forces on the hand can imply different actions when the robot is pulling a handle, holding position or releasing an object.",
          "A controller can also confuse a desired reaction with a disturbance. During wiping, reducing every contact force would remove the pressure needed to clean the surface. During a collision, maintaining pressure could prolong unwanted contact. The task state and the contact location need to remain connected through the control stack."
        ]
      },
      {
        "heading": "A force-attribution test worth reporting",
        "paragraphs": [
          "An additional evaluation could hold the commanded task constant while varying where a measured external load enters the robot. Test the gripper, forearm and body separately, then include simultaneous contacts. Use instrumented loading within the platform's approved limits. This is a proposed evaluation, not a test reported as completed here.",
          "For each trial, record the estimated source of contact, force-estimation error, body displacement, task completion and any operator intervention. A confusion table would show whether a body disturbance is repeatedly interpreted as hand contact. The score should keep those mistakes visible even when the robot eventually completes the task."
        ]
      },
      {
        "heading": "Separating estimation from compensation",
        "paragraphs": [
          "A better completion rate can come from several changes. The estimator might detect contact sooner. The action policy might choose a different arm motion. The whole-body controller might accept a larger displacement. Comparing only the final success rate hides these mechanisms.",
          "A useful ablation holds the task policy and controller settings constant while changing one force input at a time. Record the time from physical contact to the first corrective command. Keep the failed attempts, including trials where compensation stabilizes the body but loses the handle. A recovered stance and a completed manipulation are separate outcomes."
        ]
      },
      {
        "heading": "What changes on a humanoid",
        "paragraphs": [
          "A biped may transfer weight between feet while its hand remains in contact with the environment. The arm load, foot contacts and support geometry then change together. A wheeled-legged result gives researchers a method to investigate, but does not supply a humanoid success rate.",
          "For a humanoid deployment, the evidence should include the same task at different arm reaches, support stances and payloads. Reports should state whether the robot needed a harness, external tracking or an operator. Those details determine how much of the result carries into a different workspace."
        ]
      }
    ],
    "seoTitle": "FWBC-VLA: Force-Aware Whole-Body Robot Control | TechniaHQRobot",
    "slug": "fwbc-vla-force-aware-contact-control",
    "sourceLinks": [
      {
        "documentTitle": "FWBC-VLA Force-Aware Whole-Body Compensation for Contact-Rich Loco-Manipulation",
        "label": "FWBC-VLA paper",
        "organization": "Zhang and colleagues",
        "url": "https://arxiv.org/abs/2609.03889"
      },
      {
        "label": "FWBC-VLA project",
        "organization": "FWBC-VLA authors",
        "url": "https://ytydt-reuz.github.io/FWBC-VLA/"
      }
    ],
    "title": "FWBC-VLA and the Problem of Knowing Where a Force Comes From",
    "id": "fwbc-vla-force-aware-contact-control",
    "path": "/robotics-news/fwbc-vla-force-aware-contact-control",
    "datePublished": "2026-09-13",
    "dateModified": "2026-09-13",
    "schemaType": "Article",
    "authorName": "TechniaHQRobot",
    "showTableOfContents": true,
    "readingTime": "3 min read"
  },
  {
    "category": "Robot policy execution",
    "description": "Why action chunks can clash during robot motion, how RTC joins them, and which latency measurements belong in a Physical AI evaluation.",
    "introduction": [
      "A robot arm keeps moving while a vision-language-action model calculates its next commands. By the time the answer arrives, the arm has left the pose shown in the input image. Starting the new trajectory from that old pose can produce a jump in the commanded motion.",
      "Real-time chunking, or RTC, addresses the handoff between those command sequences. It is useful to separate this execution problem from the model's ability to recognize objects or understand the instruction. A policy can choose the right task and still produce a poor handoff."
    ],
    "keywords": [
      "real-time chunking",
      "VLA inference latency",
      "robot action chunks",
      "Physical AI"
    ],
    "methodology": [
      "Analysis of the two linked RTC studies. The 50 Hz timing example is an illustrative calculation, not a measured robot result."
    ],
    "relatedLinks": [
      {
        "label": "Flow matching in robot control",
        "path": "/robotics-news/flow-matching-robot-control"
      },
      {
        "label": "Humanoid network latency",
        "path": "/humanoid-robot-network-latency"
      },
      {
        "label": "TurboVLA execution architecture",
        "path": "/robotics/turbovla-real-time-vla-robot-control"
      }
    ],
    "sections": [
      {
        "heading": "The commands already in motion",
        "paragraphs": [
          "Physical Intelligence's June 2025 RTC work generates the next action chunk while the current one executes. It fixes the portion corresponding to already committed actions and predicts the remaining motion around that constraint. The method applies inference-time conditioning to diffusion and flow policies.",
          "The team's public account describes precise tasks including inserting an Ethernet cable and lighting a candle with a match. Its latency examples total 139 ms for a mobile setup and 108 ms for a static setup. These numbers include preprocessing and transport as well as model computation. They describe the reported configurations, rather than a universal RTC latency."
        ]
      },
      {
        "heading": "Why averaging two trajectories can go wrong",
        "paragraphs": [
          "Imagine two valid approaches around an obstacle. One passes to the left and the other to the right. Averaging their positions can put the resulting path through the obstacle. A smooth numerical signal does not by itself establish a valid physical trajectory.",
          "The same issue appears when the model switches its intended grasp or changes the direction of a wrist movement. Continuity needs to preserve the actions that will occur before the new result arrives. It also needs to leave room to respond to a changed observation. That balance depends on the command horizon and measured delay."
        ]
      },
      {
        "heading": "The training-time follow-up",
        "paragraphs": [
          "A December 2025 follow-up moves action-prefix conditioning into training. The authors simulate inference delay and train the policy to condition directly on committed actions. This removes the extra inference-time inpainting calculation used by the earlier approach.",
          "The paper evaluates box building and espresso preparation with a pi-0.6 policy. It reports comparable task performance and speed to inference-time RTC with lower computational cost in those experiments. The method still requires a policy trained or fine-tuned with the appropriate conditioning. It does not remove camera, network or actuator delay."
        ]
      },
      {
        "heading": "Measure the age of the command",
        "paragraphs": [
          "A deployment report should start the clock when the observation is captured and stop it when the matching command begins execution. Timing only the model call leaves out frame transfer, image processing, queues and controller scheduling. Those omitted stages can determine whether a command is still relevant.",
          "For an illustrative 50 Hz controller, each control interval lasts 20 ms. A 100 ms delay spans five intervals. This arithmetic does not tell us whether a task will succeed. It tells us how much of the earlier sequence may execute before a new result can be used. A runtime must also handle delay variation, rather than assuming every call lasts exactly 100 ms."
        ]
      },
      {
        "heading": "Report useful work and failed handoffs",
        "paragraphs": [
          "Compare execution methods using the same policy checkpoint, robot, task setup and stop conditions. Publish completion counts, elapsed time and the number of interrupted attempts. Include the distribution of end-to-end delay and the largest command discontinuities at chunk boundaries.",
          "A fast failed grasp should remain a failed grasp in the report. Likewise, a run that finishes after an operator repositions the object needs an intervention label. These records help distinguish shorter idle periods from better task execution.",
          "On a humanoid, whole-body balance may run through a separate controller. RTC in the manipulation policy does not establish a stopping-distance guarantee or a fall-recovery capability. Those behaviors require their own measurements with the actual body, payload and controller."
        ]
      }
    ],
    "seoTitle": "Real-Time Chunking: VLA Inference Latency | TechniaHQRobot",
    "slug": "real-time-chunking-vla-latency",
    "sourceLinks": [
      {
        "label": "Physical Intelligence RTC research",
        "organization": "Physical Intelligence",
        "url": "https://www.pi.website/research/real_time_chunking"
      },
      {
        "label": "Training-time RTC paper",
        "organization": "Black, Ren, Equi and Levine",
        "url": "https://arxiv.org/abs/2512.05964"
      },
      {
        "label": "RTC experimental code",
        "organization": "Physical Intelligence",
        "url": "https://github.com/Physical-Intelligence/real-time-chunking-kinetix"
      }
    ],
    "title": "How Real-Time Chunking Keeps a Robot Moving During VLA Inference",
    "id": "real-time-chunking-vla-latency",
    "path": "/robotics-news/real-time-chunking-vla-latency",
    "datePublished": "2026-09-13",
    "dateModified": "2026-09-13",
    "schemaType": "Article",
    "authorName": "TechniaHQRobot",
    "showTableOfContents": true,
    "readingTime": "3 min read"
  },
  {
    "category": "Robot evaluation",
    "description": "A 2026 audit examines shortcuts, statistical evidence and training-data effects in LIBERO, CALVIN, SimplerEnv, RoboCasa and RoboTwin 2.0.",
    "introduction": [
      "A robot policy's success rate describes a particular set of trials. The scene layouts, object choices, training data and stopping rules all help define that number. A score can rise while the capability a reader cares about remains untested.",
      "A June 2026 study audits five manipulation benchmarks and asks how well their scores support broader claims. It provides a useful way to read Physical AI papers before turning a leaderboard result into a claim about a robot working in a home or factory."
    ],
    "keywords": [
      "robot manipulation benchmark",
      "LIBERO",
      "CALVIN",
      "Physical AI evaluation"
    ],
    "methodology": [
      "The findings attributed to the audit come from its authors. The evaluation-card proposal and numerical sampling example are TechniaHQRobot analysis."
    ],
    "relatedLinks": [
      {
        "label": "Robot testing standards",
        "path": "/humanoid-robot-testing-standards"
      },
      {
        "label": "Robot dataset standards",
        "path": "/humanoid-robot-dataset-standards"
      },
      {
        "label": "Zero-shot robotics",
        "path": "/robotics-news/zero-shot-robotics-physical-ai"
      }
    ],
    "sections": [
      {
        "heading": "The audit's four diagnostics",
        "paragraphs": [
          "The researchers examine shortcut solvability, statistical evidence, repeated adaptation to the test distribution and dependence on the source of training data. They apply these diagnostics to LIBERO, CALVIN, SimplerEnv, RoboCasa and RoboTwin 2.0.",
          "On LIBERO, they report that a 0.09B probe without a language encoder reaches results near leading reported scores. This challenges how much a high aggregate score alone establishes about language-conditioned behavior. It does not show that language is unnecessary for every manipulation task."
        ]
      },
      {
        "heading": "Changing the scene inside the training range",
        "paragraphs": [
          "The project's CALVIN analysis changes the block poses while remaining within the training range. The reported average number of completed tasks out of five falls from 4.17 to 3.14. That result motivates looking at the distribution of starting states, rather than only the named task suite.",
          "A narrow recurring arrangement can reward a policy that depends on familiar placement. For a reader interested in deployment, the next questions concern the size and direction of position changes, whether objects stay visible and whether the same object identities appear during training. Each condition defines a different generalization claim."
        ]
      },
      {
        "heading": "A percentage needs its trial record",
        "paragraphs": [
          "Two papers can report the same percentage after using different numbers of attempts, different task mixtures or different reset rules. The percentages alone leave the reader unable to reconstruct the comparison. Publish successes and attempts per task, the tested seeds and the success detector.",
          "For example, 90 successes from 100 attempts and 900 from 1,000 both produce 90 percent. The second sample contains more information about uncertainty under the same sampling assumptions. Repeated runs from one fixed scene also differ from independent runs across varied scenes. More repeats of the same situation do not automatically establish wider coverage.",
          "A comparison between two policies should explain whether they used paired starting states. A failed reset, simulator crash or timed-out episode needs a stated treatment. Excluding difficult episodes after observing the result can alter the reported success rate."
        ]
      },
      {
        "heading": "Keep training provenance attached to the score",
        "paragraphs": [
          "Record the dataset release, task-specific fine-tuning data, object identities and checkpoint selection procedure. The audit shows why closeness between training examples and test conditions can make a smaller model look competitive with a larger model trained on broader data.",
          "This also affects claims about open models. Releasing weights lets another team run the policy, but a missing training-data description can still prevent that team from interpreting a benchmark gain. Model size, compute and data coverage belong beside the score."
        ]
      },
      {
        "heading": "An evaluation card a reader can use",
        "paragraphs": [
          "A practical result card would identify the model checkpoint, simulator version, task list, environment seeds, training sources, action frequency and time limit. For each task it would provide successes, attempts, intervention counts and a description of the success detector.",
          "A second evaluation can vary one condition at a time. Move an object, change its identity, alter the camera position or rewrite the instruction while retaining its meaning. Report the size of each change. This proposed reporting format makes failure patterns visible without collapsing every variation into one average.",
          "Simulation remains useful for controlled comparisons. A humanoid working with physical objects also needs tests for contact, sensor delay, actuator limits and recovery. The audit supplies diagnostics for interpreting a score; it does not certify a physical robot for deployment."
        ]
      }
    ],
    "seoTitle": "Robot Benchmark Audits: Shortcuts and Gaps | TechniaHQRobot",
    "slug": "robot-manipulation-benchmark-audit",
    "sourceLinks": [
      {
        "label": "Manipulation benchmark audit project",
        "organization": "Jiang and colleagues",
        "url": "https://ripl.github.io/manipulation_benchmark_audit/"
      },
      {
        "label": "June 2026 audit paper",
        "organization": "Toyota Technological Institute at Chicago and collaborators",
        "url": "https://arxiv.org/abs/2606.04233"
      }
    ],
    "title": "What a High Robot Manipulation Benchmark Score Can Hide",
    "id": "robot-manipulation-benchmark-audit",
    "path": "/robotics-news/robot-manipulation-benchmark-audit",
    "datePublished": "2026-09-13",
    "dateModified": "2026-09-13",
    "schemaType": "Article",
    "authorName": "TechniaHQRobot",
    "showTableOfContents": true,
    "readingTime": "3 min read"
  },
  {
    "slug": "reactivebfm-g1-closed-loop-replanning",
    "title": "ReactiveBFM Lets a Unitree G1 Replan When the Target Moves",
    "seoTitle": "ReactiveBFM: Unitree G1 Replanning and Recovery | TechniaHQRobot",
    "description": "What ReactiveBFM reports about moving-target reaching, recovery and text-conditioned motion, with the simulation score kept separate from physical tests.",
    "category": "Humanoid motion research",
    "keywords": [
      "ReactiveBFM",
      "Unitree G1",
      "closed-loop humanoid control",
      "whole-body planning"
    ],
    "introduction": [
      "Reaching for a moving target requires the robot to revise a movement after it has started. Its next plan must begin from the body pose it actually reached. A reference trajectory based on an earlier pose can become less useful as tracking errors accumulate.",
      "ReactiveBFM studies that feedback loop on the Unitree G1. The project shows online target reaching, changes to text-conditioned motion and recovery after disturbances. Reading those examples alongside the evaluation conditions gives a more useful picture of the controller than a collection of successful movements alone."
    ],
    "sections": [
      {
        "heading": "Planning from an imperfect body state",
        "paragraphs": [
          "The authors train the motion planner with a scheduled prefix-sampling curriculum. The aim is to expose it to imperfect physical states rather than relying only on reference motion. An asynchronous replanning mechanism connects the slower planning process to the faster tracking process.",
          "The project describes a closed-loop planning and control framework. The planner can revise the future motion as observations change. This differs from evaluating only whether a tracker can reproduce a fixed motion from its expected starting state."
        ]
      },
      {
        "heading": "What the physical examples show",
        "paragraphs": [
          "The Unitree G1 experiments include reaching toward a moving target and recovering from disturbances. The authors describe moving-target reaching as zero-shot transfer from training on static targets. Their project page also presents streaming text commands and several motion categories.",
          "These examples provide evidence about the motions shown under the authors' setup. The published 93.1 percent success figure is a sim-to-sim result under perturbations. It should keep that simulation label wherever it appears, including a headline, image caption or comparison table. It is not a measured household-task completion rate."
        ]
      },
      {
        "heading": "A moving target adds several separate tests",
        "paragraphs": [
          "A target can move farther away, change direction, become occluded or leave the reachable workspace. A controller may handle one of these cases and fail on another. A useful result therefore specifies the target trajectory, its speed, the starting hand position and the robot's allowed foot motion.",
          "The end of a reach also needs a definition. Touching a target once differs from maintaining contact while it moves. A body that reaches the target after stepping outside the permitted region may satisfy one detector and fail the application. The target detector, workspace boundary and stopping condition should be documented together."
        ]
      },
      {
        "heading": "Recovery should preserve the task",
        "paragraphs": [
          "Remaining upright after a disturbance is one outcome. Resuming the requested movement is another. A controller can regain balance while forgetting which hand was meant to reach, switching the target or continuing the wrong motion category.",
          "An additional evaluation could log the disturbance time, the first updated plan, foot displacement and the time to return to the requested task. It should include interrupted trials and operator resets. This proposed measurement separates balance recovery from command retention without assuming they improve together.",
          "A sequence of commands also needs a policy for cancellation. When a new instruction arrives during a reach, the system must decide which parts of the existing movement remain committed. Tests should distinguish an instruction accepted by the language interface from a change that has reached the physical controller."
        ]
      },
      {
        "heading": "The evidence needed for another robot",
        "paragraphs": [
          "Porting the approach to a different humanoid changes joint limits, reach, inertial properties, sensing delay and contact behavior. A planner trained around one body's feasible movements cannot be assumed to preserve the same behavior after a hardware swap.",
          "A reproducible report should identify the robot configuration, control rates, observation sources, policy checkpoint, tracking equipment and any supporting harness. Keep simulation trials and physical trials in separate tables. Add counts of falls, completed reaches and manual resets to the physical table.",
          "For practical use, the next evidence would be repeated runs across target paths and support conditions that were fixed before evaluation. That would let another team compare its own controller against the same task instead of trying to infer performance from edited footage."
        ]
      }
    ],
    "sourceLinks": [
      {
        "label": "ReactiveBFM paper",
        "url": "https://arxiv.org/abs/2606.30362",
        "organization": "Chen and colleagues"
      },
      {
        "label": "ReactiveBFM experiments and project",
        "url": "https://xiao-chen.tech/reactivebfm/",
        "organization": "Chinese University of Hong Kong and Shanghai AI Laboratory"
      }
    ],
    "relatedLinks": [
      {
        "label": "Fall protection and recovery",
        "path": "/humanoid-robot-fall-protection"
      },
      {
        "label": "Whole-body loco-manipulation",
        "path": "/robotics-news/whole-body-loco-manipulation"
      },
      {
        "label": "Humanoid autonomy levels",
        "path": "/humanoid-robot-autonomy-levels"
      }
    ],
    "methodology": [
      "Analysis of the authors' paper and project page. Suggested reporting fields are editorial proposals. No independent physical trial was performed for this article."
    ],
    "id": "reactivebfm-g1-closed-loop-replanning",
    "path": "/robotics-news/reactivebfm-g1-closed-loop-replanning",
    "datePublished": "2026-09-13",
    "dateModified": "2026-09-13",
    "schemaType": "Article",
    "authorName": "TechniaHQRobot",
    "showTableOfContents": true,
    "readingTime": "3 min read"
  },
  {
    "slug": "robot-camera-joint-timestamp-synchronization",
    "title": "A Robot Can Miss a Grasp Because Its Camera and Joint Data Disagree on Time",
    "seoTitle": "Robot Camera and Joint Timestamp Alignment | TechniaHQRobot",
    "description": "How mismatched camera, proprioception and action timestamps affect grasping, and what to record before training or deploying a robot policy.",
    "category": "Robot sensing and data",
    "keywords": [
      "robot sensor synchronization",
      "camera latency",
      "proprioception timestamps",
      "robot data quality"
    ],
    "introduction": [
      "A camera frame and a joint-state message can arrive together while describing different moments. The picture may show the gripper approaching an object. The joint message may already describe a later pose. Combining those observations creates a state that never existed on the robot.",
      "This timing error can enter both training data and live control. A policy may then learn the wrong relationship between a visible contact and the command that caused it. Before adding more recordings, it is useful to establish what time each stream actually represents."
    ],
    "sections": [
      {
        "heading": "Capture time and arrival time",
        "paragraphs": [
          "Keep the timestamp for sensor acquisition separate from the time the host receives the message. Camera exposure, internal buffering, compression and transport can all sit between them. A software timestamp written after reception cannot reconstruct an earlier acquisition time unless the relevant delay is measured.",
          "Joint observations and gripper commands add their own timing conventions. A commanded position is a target. A measured position describes an observation. Record both with explicit units and clock domains so that later processing can distinguish intent from motion."
        ]
      },
      {
        "heading": "What UMI measures",
        "paragraphs": [
          "The Universal Manipulation Interface paper includes separate procedures for camera latency, proprioception latency, gripper execution latency and robot execution latency. Its policy interface accounts for inference-time delay when transferring data from handheld grippers to robot execution.",
          "That separation is useful because one latency value cannot describe every stage of the chain. A camera replacement can change observation timing without changing actuator response. A controller update can alter execution timing while the recorded video stream remains the same. The UMI paper and implementation provide a concrete reference for studying these stages."
        ]
      },
      {
        "heading": "How a small delay becomes a spatial error",
        "paragraphs": [
          "Consider an illustrative object moving at a constant 0.5 metres per second. During a 100 ms offset it travels 5 cm. At a 20 ms offset it travels 1 cm. These distances follow from speed multiplied by time. They are examples, not measurements from a particular robot.",
          "Rotation adds another source of mismatch. A wrist camera rotates with the arm, changing where an object appears even when the object stays still. A time offset between the image and the pose used to interpret it can therefore look like a calibration error.",
          "This is one reason to check timing and geometry separately. Improving a static camera calibration does not remove a variable delay. Conversely, synchronized timestamps do not correct an inaccurate camera-to-robot pose estimate."
        ]
      },
      {
        "heading": "A useful timing record",
        "paragraphs": [
          "For every sensor and command stream, document the clock source, timestamp unit, sampling rate, sequence identifier and expected buffering. Preserve missing frames and time discontinuities in a quality report. Silently filling gaps can hide the conditions that later cause a policy to hesitate or miss a contact.",
          "A timing check should compare repeated events visible in the relevant streams. Review the estimated offset over the whole recording, rather than measuring it once at startup. Drift and occasional buffering spikes can produce errors that a single average conceals.",
          "Store the synchronization method alongside the dataset version. A future reader should be able to tell whether alignment used hardware triggers, a common clock, recorded timing markers or a measured software offset. State the remaining uncertainty instead of labelling every aligned sample exact."
        ]
      },
      {
        "heading": "Check again during execution",
        "paragraphs": [
          "After preprocessing, confirm that the live runtime uses the same observation and action conventions as training. Queue depth, video processing and remote inference can change after deployment. A correct offline dataset does not establish the age of a command arriving at a motor controller.",
          "Measure distributions across representative operating conditions. Record the camera exposure-to-policy delay and the observation-to-execution delay separately. Include the upper tail and the number of stale samples rejected by the runtime.",
          "For a humanoid, keep the low-level balance loop's timing requirements separate from those of a camera-based manipulation policy. Each controller should consume observations under its own defined age limits. Changes to sensors, firmware or buffering should trigger a new timing check before comparing task performance."
        ]
      }
    ],
    "sourceLinks": [
      {
        "label": "UMI paper and latency procedures",
        "url": "https://arxiv.org/abs/2402.10329",
        "organization": "Chi and colleagues"
      },
      {
        "label": "Universal Manipulation Interface project",
        "url": "https://umi-gripper.github.io/",
        "organization": "Stanford University, Columbia University and Toyota Research Institute"
      }
    ],
    "relatedLinks": [
      {
        "label": "Humanoid calibration",
        "path": "/humanoid-robot-calibration"
      },
      {
        "label": "Robot dataset standards",
        "path": "/humanoid-robot-dataset-standards"
      },
      {
        "label": "Real-time chunking",
        "path": "/robotics-news/real-time-chunking-vla-latency"
      }
    ],
    "methodology": [
      "Engineering analysis informed by the linked UMI work. The distance examples assume constant linear speed. They are calculations supplied to explain timing error."
    ],
    "id": "robot-camera-joint-timestamp-synchronization",
    "path": "/robotics-news/robot-camera-joint-timestamp-synchronization",
    "datePublished": "2026-09-13",
    "dateModified": "2026-09-13",
    "schemaType": "Article",
    "authorName": "TechniaHQRobot",
    "showTableOfContents": true,
    "readingTime": "3 min read"
  },
  {
    "slug": "sonicsense-robot-acoustic-touch",
    "title": "SonicSense Uses Fingertip Vibrations to Inspect Objects",
    "seoTitle": "SonicSense: Acoustic Touch for Robot Hands | TechniaHQRobot",
    "description": "How a four-finger robot hand uses contact microphones to inspect objects, and which noise, contact and object changes an acoustic sensing test should cover.",
    "category": "Robot perception research",
    "keywords": [
      "SonicSense",
      "acoustic tactile sensing",
      "robot hand",
      "contact microphones"
    ],
    "introduction": [
      "A closed container can hide information from a camera. Moving it or tapping its surface produces another observation through vibration. A robot can use that signal to investigate the object, provided it knows how the contact was made.",
      "SonicSense, developed by Jiaxun Liu and Boyuan Chen at Duke University, explores this approach with a robot hand. It connects sensing to an action the robot performs on the object, which makes the quality of that action part of the measurement."
    ],
    "sections": [
      {
        "heading": "What the hand physically does",
        "paragraphs": [
          "Duke describes a four-finger hand with a contact microphone in each fingertip. The hand taps, grasps and shakes objects while recording vibrations transmitted through contact. The system uses the resulting signals to infer properties of the object.",
          "The research evaluates 83 real-world objects. Its reported tasks include material prediction, shape reconstruction, object re-identification and distinguishing container inventory states. The paper describes an exploration policy and learned models for processing the observations. These results cover the authors' object set and evaluation protocol."
        ]
      },
      {
        "heading": "The contact is part of the sensor",
        "paragraphs": [
          "A vibration measurement depends on where the finger touches, how hard it presses and which other fingers support the object. A weak contact can attenuate the signal. A different support can change how the object vibrates. Those changes can occur while the camera image appears almost identical.",
          "An evaluation should therefore record the interaction alongside the audio. The commanded motion, measured contact state and microphone sample window help another researcher understand the signal. A material label without that context can conceal variation caused by the hand itself."
        ]
      },
      {
        "heading": "What contact microphones contribute",
        "paragraphs": [
          "A fingertip contact microphone measures vibrations transmitted through the object and finger. This gives it a different input from a microphone listening through the surrounding air. Duke's account explains the use of direct contact to reduce the influence of ambient sound.",
          "Direct contact still leaves other sources of variation to investigate. The robot's motors and structure can transmit vibration, and the contact surface can change through wear. Treat rejection of room noise as one test condition, then measure how these other paths affect the recorded signal."
        ]
      },
      {
        "heading": "Hold out complete objects during testing",
        "paragraphs": [
          "A system that recognizes another recording of a familiar object has passed a different test from one that classifies a new object. Splitting repeated taps from the same object between training and test sets can blur that distinction.",
          "A useful evaluation would identify which complete objects are held out, along with material, geometry and container contents. It could then repeat the same object under different grasp positions and support conditions. These are proposed reporting steps for judging transfer, rather than additional SonicSense results.",
          "For container inspection, publish the tested fill states and the allowed motions. A container with loose rigid pieces presents a different sensing problem from one holding a viscous liquid. A general phrase such as content recognition cannot convey those differences."
        ]
      },
      {
        "heading": "From sensing to a manipulation decision",
        "paragraphs": [
          "An acoustic estimate becomes useful when it changes a later action. A robot might request another observation, choose a handling motion or flag an uncertain item for review. The sensing paper should be read separately from any claim that a robot can complete an entire sorting or household workflow.",
          "For a deployed system, measure the number of interactions required, the time spent inspecting, object damage and the rate of incorrect decisions. Include an unknown-object outcome so that every unfamiliar signal does not have to receive a confident familiar label.",
          "A humanoid hand also has packaging constraints. Finger movement, cable routing, mechanical protection and contact material can alter the signal path. A successful laboratory hand gives engineers a sensing approach to investigate. Evidence from the final hand and the intended object range is still needed to judge repeated use."
        ]
      }
    ],
    "sourceLinks": [
      {
        "label": "SonicSense research paper",
        "url": "https://arxiv.org/abs/2406.17932",
        "organization": "Jiaxun Liu and Boyuan Chen"
      },
      {
        "label": "Duke report on the SonicSense hand",
        "url": "https://pratt.duke.edu/news/sonicsense-robotic-hand/",
        "organization": "Duke University",
        "date": "2024-10-22"
      },
      {
        "label": "SonicSense project",
        "url": "https://generalroboticslab.com/SonicSense/",
        "organization": "General Robotics Lab"
      }
    ],
    "relatedLinks": [
      {
        "label": "Tactile foundation models",
        "path": "/robotics-news/tactile-foundation-models"
      },
      {
        "label": "Humanoid hand durability",
        "path": "/humanoid-robot-hand-durability"
      },
      {
        "label": "Robot sensor families",
        "path": "/robot-sensors"
      }
    ],
    "methodology": [
      "Research analysis based on the SonicSense paper and Duke's report. Proposed evaluation conditions describe further checks, not experiments performed by TechniaHQRobot."
    ],
    "id": "sonicsense-robot-acoustic-touch",
    "path": "/robotics-news/sonicsense-robot-acoustic-touch",
    "datePublished": "2026-09-13",
    "dateModified": "2026-09-13",
    "schemaType": "Article",
    "authorName": "TechniaHQRobot",
    "showTableOfContents": true,
    "readingTime": "3 min read"
  }
];
